import { z } from "zod";
import { LOCATION_TYPES } from "../enums.ts";
import { Address, DateOnly, Money, TimeOnly } from "./common.ts";

/* ------------------------------ schedule windows ------------------------------ */
export const WindowQuery = z.object({
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  personId: z.string().uuid().optional(),
  locationType: z.enum(LOCATION_TYPES).optional(),
  locationId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  missed: z.string().optional(),
  mine: z.string().optional(),
});
export const WindowUpdateInput = z.object({
  personId: z.string().uuid().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export const WindowInput = z.object({
  locationType: z.enum(LOCATION_TYPES),
  locationId: z.string().uuid(),
  personId: z.string().uuid().nullable().optional(),
  startsOn: DateOnly,
  endsOn: DateOnly,
  notes: z.string().max(2000).nullable().optional(),
});

/* ------------------------------------ events ----------------------------------- */
export const KosherProfile = z
  .object({
    certificationStatus: z.string().max(40).nullable().optional(),
    kosherForPesach: z.boolean().nullable().optional(),
    passoverUse: z.string().max(200).nullable().optional(),
    cholovYisroel: z.boolean().nullable().optional(),
    pasYisroel: z.boolean().nullable().optional(),
    mevushal: z.boolean().nullable().optional(),
    fish: z.boolean().nullable().optional(),
    kitniyos: z.boolean().nullable().optional(),
    restriction: z.string().max(60).nullable().optional(),
    kosherGroup: z.string().max(60).nullable().optional(),
    certificateComment: z.string().max(2000).nullable().optional(),
  })
  .partial();

export const CatererInput = z.object({
  name: z.string().min(1).max(200),
  companyId: z.string().uuid().nullable().optional(),
  contactName: z.string().max(200).nullable().optional(),
  phone: z.string().max(60).nullable().optional(),
  email: z.string().email().max(200).nullable().optional(),
  billingEmail: z.string().email().max(200).nullable().optional(),
  address: Address.nullable().optional(),
  defaultMashgiachFee: Money.nullable().optional(),
  defaultInvoiceAmount: Money.nullable().optional(),
  kosherProfile: KosherProfile.optional(),
  active: z.boolean().default(true),
  notes: z.string().max(4000).nullable().optional(),
});
export const CatererUpdateInput = CatererInput.partial();

export const EventInput = z.object({
  catererId: z.string().uuid(),
  companyId: z.string().uuid().nullable().optional(),
  date: DateOnly,
  startTime: TimeOnly.nullable().optional(),
  endTime: TimeOnly.nullable().optional(),
  venueName: z.string().max(200).nullable().optional(),
  venueAddress: Address.nullable().optional(),
  kosherProfile: KosherProfile.optional(),
  mashgiachPersonId: z.string().uuid().nullable().optional(),
  mashgiachFee: Money.nullable().optional(),
  invoiceAmount: Money.nullable().optional(),
  templateKey: z.string().max(80).default("event_v1"),
  notes: z.string().max(4000).nullable().optional(),
  /** a second event for the same caterer, date and venue is surfaced as probable — this confirms it is real */
  confirmDuplicate: z.boolean().default(false),
});
export const EventUpdateInput = EventInput.partial();
export const EventQuery = z.object({
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  catererId: z.string().uuid().optional(),
  stage: z.string().max(40).optional(),
  billing: z.string().max(40).optional(),
  mashgiach: z.string().max(40).optional(),
  stale: z.string().optional(),
  mine: z.string().optional(),
});
export const EVENT_STAGES = ["booked", "mashgiach_assigned", "report_filed", "complete"] as const;
export const EVENT_BILLING_STATUSES = ["not_billed", "invoiced", "paid", "not_billable"] as const;
export const EVENT_MASHGIACH_STATUSES = ["none_submitted", "submitted", "approved", "paid", "nothing_owed"] as const;
export const EventBillingInput = z.object({ status: z.enum(EVENT_BILLING_STATUSES), invoiceNumber: z.string().max(60).nullable().optional(), note: z.string().max(1000).optional() });
export const EventMashgiachInput = z.object({ status: z.enum(EVENT_MASHGIACH_STATUSES), note: z.string().max(1000).optional() });

/* -------------------------------- purchase orders ------------------------------ */
export const PurchaseOrderInput = z.object({
  companyId: z.string().uuid(),
  locationType: z.enum(LOCATION_TYPES).default("plant"),
  locationId: z.string().uuid(),
  poNumber: z.string().min(1).max(80),
  amount: Money.nullable().optional(),
  receivedOn: DateOnly,
  requestedFor: DateOnly.nullable().optional(),
  documentId: z.string().uuid().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export const PurchaseOrderUpdateInput = PurchaseOrderInput.partial();
export const PurchaseOrderQuery = z.object({
  companyId: z.string().uuid().optional(),
  locationType: z.enum(LOCATION_TYPES).optional(),
  locationId: z.string().uuid().optional(),
  open: z.string().optional(),
});

/* ------------------------------------ split ------------------------------------ */
export const SplitInput = z.object({
  chargeTotal: Money,
  chargeNote: z.string().max(1000).nullable().optional(),
  parts: z.array(z.object({ companyId: z.string().uuid(), amount: Money, purchaseOrderId: z.string().uuid().nullable().optional(), note: z.string().max(1000).nullable().optional() })).min(1),
});

/* --------------------------------- admin fees --------------------------------- */
export const AdminFeeInput = z.object({
  method: z.enum(["flat", "percent"]),
  value: z.number().min(0).max(1_000_000),
  active: z.boolean().default(true),
  note: z.string().max(1000).nullable().optional(),
});

/* ------------------------------- mashgiach invoices --------------------------- */
export const WORK_KINDS = ["production", "food_service", "event", "other"] as const;
export const MashgiachInvoiceInput = z.object({
  inspectionId: z.string().uuid().nullable().optional(),
  eventId: z.string().uuid().nullable().optional(),
  billDate: DateOnly.optional(),
  dayFee: Money.default(0),
  mileageMiles: z.number().min(0).max(5000).default(0),
  expenses: Money.default(0),
  expenseNote: z.string().max(1000).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export const MashgiachInvoiceQuery = z.object({
  mine: z.string().optional(),
  status: z.string().max(40).optional(),
  gate: z.string().max(40).optional(),
  personId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  workKind: z.string().max(40).optional(),
});
export const ApproveInvoiceInput = z.object({
  visitAdminFeeMethod: z.enum(["flat", "percent"]).nullable().optional(),
  visitAdminFeeValue: z.number().min(0).nullable().optional(),
  customerCharge: Money.nullable().optional(),
  note: z.string().max(1000).optional(),
});
export const RejectInvoiceInput = z.object({ reason: z.string().min(1).max(1000) });
export const FrontInvoiceInput = z.object({ reason: z.string().min(1).max(1000) });
export const MarkPaidInput = z.object({ paidRef: z.string().max(120).nullable().optional() });
export const MonthlyRunInput = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/) });
